import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    FilePenLine,
    Search,
    Users2,
    Plus,
    ToggleLeft,
    ToggleRight,
    Download
} from 'lucide-react';
import constant from '../../constant';
import FileSaver from 'file-saver';
import Papa from 'papaparse';

const ItemsTable = ({ items, onDelete, onEdit }) => {
    const [statusFilter, setStatusFilter] = useState('active');
    const [searchQuery, setSearchQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [localItems, setLocalItems] = useState(items);
    const [selectedItems, setSelectedItems] = useState([]);
    const userRole = localStorage.getItem("role");

    useEffect(() => {
        const filteredItemIds = localItems
            .filter(item => selectedItems.includes(item._id))
            .map(item => item._id);
        setSelectedItems(filteredItemIds);
    }, [localItems]);

    const handleToggleStatus = async (itemId, currentStatus) => {
        setLocalItems((prevItems) =>
            prevItems.map((item) =>
                item._id === itemId ? { ...item, isActive: !currentStatus } : item
            )
        );

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                alert('No authentication token found');
                return;
            }

            const response = await fetch(`${constant.REACT_APP_API_URL}/item/${itemId}`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ isActive: !currentStatus }),
            });

            if (response.ok) {
                onEdit(itemId, !currentStatus);
            } else {
                setLocalItems(prevItems =>
                    prevItems.map(item =>
                        item._id === itemId ? { ...item, isActive: currentStatus } : item
                    )
                );
                const result = await response.json();
                throw new Error(result.message || 'Failed to toggle item status');
            }
        } catch (error) {
            console.error(error);
            alert(error.message);
        }
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            setSelectedItems(localItems.map(item => item._id));
        } else {
            setSelectedItems([]);
        }
    };

    const handleRowSelect = (itemId) => {
        setSelectedItems((prevSelected) =>
            prevSelected.includes(itemId)
                ? prevSelected.filter((id) => id !== itemId)
                : [...prevSelected, itemId]
        );
    };

    const filteredItems = localItems.filter((item) => {
        const matchesStatus =
            statusFilter === 'all'
                ? true
                : statusFilter === 'active'
                    ? item.isActive
                    : !item.isActive;

        const matchesSearch =
            item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.itemType.includes(searchQuery) ||
            item.itemType.toLowerCase().includes(searchQuery.toLowerCase());

        const matchesType =
            typeFilter === 'all' || item.itemType.toLowerCase() === typeFilter;

        return matchesStatus && matchesSearch && matchesType;
    });

    const handleDownload = async () => {
        const token = localStorage.getItem('token');
        const adminSelectedUser = localStorage.getItem('adminSelectedUser');

        if (!token) {
            alert("No authentication token found");
            return;
        }

        if (!adminSelectedUser) {
            alert("No user selected for download.");
            return;
        }

        try {
            const response = await fetch(
                `${constant.REACT_APP_API_URL}/item/download/${adminSelectedUser}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (response.ok) {
                const result = await response.json();
                if (result && result.data) {
                    const csvData = convertJsonToCsv(result.data);
                    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
                    FileSaver.saveAs(blob, 'items.csv');
                } else {
                    alert("No data received for download.");
                }
            } else {
                const errorResult = await response.json();
                throw new Error(errorResult.message || "Failed to download items");
            }
        } catch (error) {
            console.error("Download error:", error);
            alert(error.message);
        }
    };

    const convertJsonToCsv = (jsonData) => {
        const csv = Papa.unparse({
            fields: ["name", "itemType", "gstRate", "price", "unit", "hsnCode", "createdAt"],
            data: jsonData.map(item => ({
                name: item.name,
                itemType: item.itemType,
                gstRate: item.gstRate,
                price: item.price,
                unit: item.unit,
                hsnCode: item.hsnCode,
                createdAt: item.createdAt,
            }))
        });
        return csv;
    };


    return (
        <div className="max-w-8xl mx-auto px-0 lg:px-8 py-8">
            {/* Header Section */}
            <div className="mb-8">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Items</h1>
                        <p className="mt-1 text-xs text-gray-500">
                            Total items ({items.length})
                        </p>
                    </div>
                    <div className="flex gap-3">
                        {userRole === 'admin' ? (
                            <button
                                onClick={handleDownload}
                                className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-md transition-colors duration-150"
                            >
                                <Download size={18} className="mr-2" />
                                Download
                            </button>
                        ) : (
                            <Link
                                to="/items/add"
                                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-md transition-colors duration-150"
                            >
                                <Plus size={18} className="mr-2" />
                                Add Item
                            </Link>
                        )}

                    </div>
                </div>

                {/* Search and Filter Section */}
                <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
                    <div className="relative w-full sm:w-96">
                        <Search
                            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                            size={20}
                        />
                        <input
                            type="text"
                            placeholder="Search items..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-150"
                        />
                    </div>
                    <div className="flex items-center gap-3 bg-white p-2 rounded-lg border border-gray-200 shadow-sm">
                        <select
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                            className="
                                block
                                w-40
                                py-2
                                px-3
                                border
                                border-gray-300
                                bg-white
                                rounded-md
                                shadow-sm
                                focus:outline-none
                                focus:ring-2
                                focus:ring-blue-500
                                focus:border-blue-500
                                text-xs
                                transition-all
                                duration-200
                            "
                        >
                            <option value="all">All Types</option>
                            <option value="product">Product</option>
                            <option value="service">Service</option>
                        </select>
                        {['all', 'active', 'inactive'].map((status) => (
                            <button
                                key={status}
                                onClick={() => setStatusFilter(status)}
                                className={`
                                    px-4
                                    py-2
                                    rounded-md
                                    text-xs
                                    font-medium
                                    transition-all
                                    duration-200
                                    ${statusFilter === status
                                        ? 'bg-blue-100 text-blue-700 ring-2 ring-blue-200'
                                        : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50 hover:shadow-sm'}
                                    focus:outline-none
                                    focus:ring-2
                                    focus:ring-blue-300
                                `}
                            >
                                {status.charAt(0).toUpperCase() + status.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Table Section */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                <div className="min-w-full overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">
                                    <input
                                        type="checkbox"
                                        checked={filteredItems.length > 0 && filteredItems.every((item) => selectedItems.includes(item._id))}
                                        onChange={handleSelectAll}
                                        className="form-checkbox h-4 w-4 text-blue-600 cursor-pointer"
                                    />
                                </th>
                                <th className="px-6 capitalize py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Item Type
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    GST Rate
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Sale Price
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Unit
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    HSN/SCN code
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                {userRole === 'admin' ? (
                                    ''
                                ) : (
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredItems.map((item) => (
                                <tr
                                    key={item._id}
                                    className="hover:bg-gray-50 transition-colors duration-150"
                                >
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <input
                                            type="checkbox"
                                            checked={selectedItems.includes(item._id)}
                                            onChange={() => handleRowSelect(item._id)}
                                            className="form-checkbox h-4 w-4 text-blue-600 cursor-pointer"
                                        />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <Link
                                            to={`/items/${item._id}`}
                                            className="flex items-center"
                                        >
                                            <div className="h-8 w-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mr-3">
                                                <Users2 size={16} />
                                            </div>
                                            <span className="text-xs font-medium text-gray-900 hover:text-blue-600">
                                                {item.name}
                                            </span>
                                        </Link>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                                        {item.itemType}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                                        {item.gstRate}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                                        {item.price}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                                        {item.unit}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                                        {item.hsnCode}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span
                                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                                            ${item.isActive
                                                    ? 'bg-green-100 text-green-800'
                                                    : 'bg-yellow-100 text-yellow-800'
                                                }
                                        `}
                                        >
                                            {item.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    {userRole === 'admin' ? (
                                        ''
                                    ) : (
                                        <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                                            <div className="flex items-center space-x-3">
                                                <Link
                                                    to={`/items/edit/${item._id}`}
                                                    className="text-blue-600 hover:text-blue-900 transition-colors duration-150"
                                                >
                                                    <FilePenLine size={18} />
                                                </Link>
                                                <button
                                                    onClick={() => handleToggleStatus(item._id, item.isActive)}
                                                    className="text-gray-600 hover:text-gray-900 transition-colors duration-150"
                                                >
                                                    {item.isActive ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                                                </button>
                                            </div>
                                        </td>
                                    )}

                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ItemsTable;